import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { ProductListProduct } from '../../api';
import { CatalogProduct, UnitOfMeasureOrderQuantity } from '../../api/models/api-shared.models';
import { CustomerProductPriceResult } from '../../api/models/customer-product-price.models';
import {
  CustomerProductRecommendation,
  GetCustomerProductRecommendationsResult,
} from '../../api/models/customer-product-recommendation.models';
import { ProductDetailTransfer } from '../../api/models/product-detail.models';
import {
  estimateProductSwitchSavings,
  getProductHasSwitchOccured,
  normalizeProductKey,
  productHasQuantity,
} from '../../helpers/general/product';
import { RootState } from '../store';

const customerProductRecommendationsAdapter = createEntityAdapter<CustomerProductRecommendation>({
  selectId: (recommendation: CustomerProductRecommendation) => recommendation.CustomerProductRecommendationId,
});

//TODO - review. ProductKey is nullable, need workaround
const catalogProductsAdapter = createEntityAdapter<CatalogProduct>({
  selectId: (product: CatalogProduct) => normalizeProductKey(product.ProductKey) || product.Id,
});

interface CustomerProductRecommendationState {
  isLoading: boolean;
  gridParameters?: { customerId: string; orderEntryHeaderId: string | undefined };
  recommendations: EntityState<CustomerProductRecommendation>;
  catalogProducts: EntityState<CatalogProduct>;
}

const initialState: CustomerProductRecommendationState = {
  isLoading: false,
  gridParameters: undefined,
  recommendations: customerProductRecommendationsAdapter.getInitialState(),
  catalogProducts: catalogProductsAdapter.getInitialState(),
};

export const customerProductRecommendationSlice = createSlice({
  name: 'customerProductRecommendation',
  initialState,
  reducers: {
    reset: () => initialState,
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setRecommendationData: (state, action: PayloadAction<GetCustomerProductRecommendationsResult>) => {
      customerProductRecommendationsAdapter.setAll(state.recommendations, action.payload.Recommendations || []);
      catalogProductsAdapter.setAll(state.catalogProducts, action.payload.Products || []);
    },
    setGridParameters: (
      state,
      action: PayloadAction<{ customerId: string; orderEntryHeaderId: string | undefined } | undefined>
    ) => {
      state.gridParameters = action.payload;
    },

    clearState: (state) => {
      customerProductRecommendationsAdapter.removeAll(state.recommendations);
      catalogProductsAdapter.removeAll(state.catalogProducts);
    },

    removeRecommendation: (state, action: PayloadAction<string>) => {
      customerProductRecommendationsAdapter.removeOne(state.recommendations, action.payload);
    },

    updateRecommendation: (
      state,
      action: PayloadAction<Partial<CustomerProductRecommendation> & { CustomerProductRecommendationId: string }>
    ) => {
      const { CustomerProductRecommendationId, ...changes } = action.payload;
      customerProductRecommendationsAdapter.updateOne(state.recommendations, {
        id: CustomerProductRecommendationId,
        changes,
      });
    },

    updateProduct: (state, action: PayloadAction<ProductDetailTransfer>) => {
      const product = state.catalogProducts.entities[normalizeProductKey(action.payload.productKey)];
      if (product) {
        product.UnitOfMeasureOrderQuantities.forEach((uom: UnitOfMeasureOrderQuantity) => {
          if (uom.UnitOfMeasure === action.payload.uom) {
            uom.ExtendedPrice = action.payload.extendedPrice;
            uom.Quantity = action.payload.quantity;
            uom.ShowOrderQuantityAlert = action.payload.showOrderQuantityAlert;
          }
        });
      }
    },

    setProductPrices: (state, action: PayloadAction<CustomerProductPriceResult[]>) => {
      action.payload.forEach((priceResult: CustomerProductPriceResult) => {
        if (priceResult.ProductKey) {
          const product = state.catalogProducts.entities[
            normalizeProductKey(priceResult.ProductKey)
          ] as ProductListProduct;
          if (product) {
            product.PriceLoaded = true;
            product.UnitOfMeasureOrderQuantities.forEach((uom: UnitOfMeasureOrderQuantity) => {
              if (uom.UnitOfMeasure === priceResult.UnitOfMeasureType) {
                uom.Price = priceResult.Price;
                if (priceResult.SecondaryUnitPrice) {
                  uom.SecondaryUnitPrice = priceResult.SecondaryUnitPrice;
                }
                if (priceResult.ExtendedPrice) {
                  uom.ExtendedPrice = priceResult.ExtendedPrice;
                }
                if (priceResult.ProductAverageWeight > 0) {
                  uom.ProductAverageWeight = priceResult.ProductAverageWeight;
                }
              }
            });
          }
        }
      });
    },
  },
});

export const {
  selectAll: selectAllRecommendations,
  selectById: selectRecommendationById,
  selectIds: selectRecommendationIds,
} = customerProductRecommendationsAdapter.getSelectors<RootState>(
  (state: RootState) => state.customerProductRecommendation.recommendations
);

export const {
  selectAll: selectAllRecommendationCatalogProducts,
  selectIds: selectAllRecommendationCatalogProductIds,
  selectById: selectRecommendationCatalogProductById,
} = catalogProductsAdapter.getSelectors<RootState>(
  (state: RootState) => state.customerProductRecommendation.catalogProducts
);

const getProductKey = (_: RootState, productKey: string) => productKey;

export const selectRecommendationByProductKey = createSelector(
  [selectAllRecommendations, getProductKey],
  (recommendations, productKey) => {
    if (!productKey) return undefined;
    return recommendations.find((rec) => normalizeProductKey(rec.ProductKey) === normalizeProductKey(productKey));
  }
);

export const selectRecommendationBySwitchProductKey = createSelector(
  [selectAllRecommendations, getProductKey],
  (recommendations, switchProductKey) => {
    if (!switchProductKey) return undefined;
    return recommendations.find(
      (rec) => normalizeProductKey(rec.SwitchProductKey) === normalizeProductKey(switchProductKey)
    );
  }
);

export const selectRandomPriorityRecommendations = createSelector(
  [selectAllRecommendations, (x: RootState) => x.customerProductRecommendation.isLoading],
  (recommendations, isLoading) => {
    if (isLoading) return [];

    const priorityRecs: CustomerProductRecommendation[] = [];
    const nonPriorityRecs: CustomerProductRecommendation[] = [];

    recommendations
      .sort(() => Math.random() - 0.5)
      .forEach((rec) => {
        if (rec.IsPriority) {
          priorityRecs.push(rec);
        } else {
          nonPriorityRecs.push(rec);
        }
      });

    const combined = [...priorityRecs, ...nonPriorityRecs];
    return combined.slice(0, 4);
  }
);

export const hasSameCreateUserDisplay = (recommendations: CustomerProductRecommendation[]): boolean => {
  if (recommendations.length === 0) return false;
  const first = recommendations[0].CreateUserDisplay;
  if (!first) return false;
  return recommendations.every((rec) => rec.CreateUserDisplay === first);
};

export const selectSwitchProductKeys = createSelector(
  [selectAllRecommendations],
  (recommendations) =>
    new Set(recommendations.filter((r) => r.SwitchProductKey).map((r) => normalizeProductKey(r.SwitchProductKey)))
);

export const selectSwitchRecommendationIds = createSelector(
  [selectAllRecommendations],
  (recommendations) =>
    new Set(
      recommendations
        .filter((r) => r.SwitchProductKey)
        .map((r) => normalizeProductKey(r.CustomerProductRecommendationId))
    )
);

export const selectSwitchRecommendationsSavings = createSelector(
  [selectAllRecommendations, (s: RootState) => s.customerProductRecommendation.catalogProducts.entities],
  (recommendations, catalogProducts) => {
    let result = {
      hasSwitchOccured: false,
      savings: 0,
      potentialSavings: 0,
      suggestedRecommendations: [] as CustomerProductRecommendation[],
    };
    const switchRecommendations = recommendations.filter((x) => x.SwitchProductKey);
    switchRecommendations.forEach((x) => {
      const currentProduct = catalogProducts?.[normalizeProductKey(x.SwitchProductKey)];
      const switchProduct = catalogProducts?.[normalizeProductKey(x.ProductKey)];

      if (currentProduct && switchProduct) {
        if (!getProductHasSwitchOccured(currentProduct, switchProduct)) {
          if (productHasQuantity(currentProduct)) {
            const switchSavings = estimateProductSwitchSavings(currentProduct, switchProduct, true, true).savings;
            if (switchSavings > 0) {
              result.suggestedRecommendations.push(x);
              result.potentialSavings += switchSavings;
            }
          }
        } else {
          result.hasSwitchOccured = true;
          result.savings += estimateProductSwitchSavings(currentProduct, switchProduct, false, true).savings;
        }
      }
    });
    return result;
  }
);

export default customerProductRecommendationSlice.reducer;
